import { useQueries, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { profitPoolUrls } from 'config/urls';
import { indexOf, orderBy } from 'lodash';
import fetchProfitPoolData from '../apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useMemo } from 'react';
import useProfitPoolFiltersLoading from './useProfitPoolFiltersLoading';

const useStatCard = () => {
    const {
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        profitPoolTierList,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);
    const isFiltersLoading = useProfitPoolFiltersLoading();

    const completeTierList = useMemo(() => {
        return profitPoolTierList.map((tier) => tier.tierReference);
    }, [profitPoolTierList]);

    const { data, isPending, isError, isSuccess } = useQueries({
        queries: [false, true].map((_bool) => {
            return {
                queryKey: [
                    'ppo-stat-card',
                    _bool,
                    completeTierList,
                    JSON.stringify(idForRestOfComponent),
                    timePeriodSelection,
                ],
                queryFn: ({ signal }) =>
                    fetchProfitPoolData({
                        selectedSubFilter: { dataKey: '', dimension: '', isAttribute: true },
                        kpi: 'profit_absolute',
                        tierList: completeTierList,
                        yearAgo: _bool,
                        groupedByList: [],
                        dimIdsFilter: idForRestOfComponent,
                        frequency: 'M',
                        timePeriod: JSON.parse(timePeriodSelection),
                        signal,
                    }),
                enabled: Boolean(
                    allDimAndAttributeTypeList &&
                        'loading' in allDimAndAttributeTypeList &&
                        !timePeriodLoading &&
                        !idForRestOfComponentLoading &&
                        timePeriodSelection,
                ),
            };
        }),
        combine: (results) => {
            let statCardData: any = [];

            const currentProfitPool = results[0]?.data?.data;
            const yearAgoProfitPool = results[1]?.data?.data;

            for (let i = 0; i < currentProfitPool?.length; i++) {
                const profit_val_current_year = currentProfitPool[i]?.kpi_values?.profit_absolute;

                const profit_val_year_ago =
                    yearAgoProfitPool?.find((_poolData: any) => {
                        return _poolData?.tier === currentProfitPool[i]?.tier;
                    })?.kpi_values?.profit_absolute || 0;

                const diff = profit_val_current_year - profit_val_year_ago;

                // Calculate percentage change only if previous year value is not zero
                let percentChange = undefined;
                if (profit_val_year_ago !== 0 && !isNaN(diff) && isFinite(diff)) {
                    const calculated = (diff / profit_val_year_ago) * 100;
                    percentChange = isFinite(calculated) ? calculated : undefined;
                }

                let currObj = {
                    tier: currentProfitPool[i].tier,
                    profit_val: profit_val_current_year,
                    profit_val_diff_year_ago: diff || undefined,
                    percent_change: percentChange,
                };

                statCardData.push(currObj);
            }
            const totalProfitVal = statCardData.reduce((acc: number, curr: any) => acc + (curr.profit_val || 0), 0);
            const totalProfitDiff = statCardData.reduce(
                (acc: number, curr: any) => acc + (curr.profit_val_diff_year_ago || 0),
                0,
            );
            const totalProfitYearAgo = totalProfitVal - totalProfitDiff;

            // Calculate total percentage change only if previous year value is not zero
            let totalPercentChange = undefined;
            if (totalProfitYearAgo !== 0 && isFinite(totalProfitDiff)) {
                const calculated = (totalProfitDiff / totalProfitYearAgo) * 100;
                totalPercentChange = isFinite(calculated) ? calculated : undefined;
            }

            let totalObj = {
                tier: 'Total',
                profit_val: totalProfitVal,
                profit_val_diff_year_ago: totalProfitDiff,
                percent_change: totalPercentChange,
            };

            statCardData.push(totalObj);

            const order = ['Total', ...completeTierList];

            const newStatCardData = orderBy(statCardData, (item) => indexOf(order, item.tier));

            return {
                data: newStatCardData,
                isPending: results.some((result) => result.isPending) || isFiltersLoading,
                isError: results.some((result) => result.isError),
                isSuccess: results.every((result) => result.isSuccess),
            };
        },
    });
    return { data, isPending, isError, isSuccess };
};

export default useStatCard;
